import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import api from '../lib/api';
import { usePersistentFilters } from '../lib/persistentFilters';
import { toast } from 'sonner';
import { Search, Truck, Plus, Trash2, Loader2, Send, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { LRReportForm, LRIssueForm } from '../components/forms';
import { usePermissions, PermissionGuard } from '../contexts/PermissionsContext';
import { useCompany } from '../contexts/CompanyContext';

const LRReportsList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedOrderId = searchParams.get('orderId');
  const { hasModulePermission } = usePermissions();
  const { activeCompany } = useCompany();
  
  const [lrReports, setLrReports] = useState([]);
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [subqualities, setSubqualities] = useState([]);
  const [transports, setTransports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = usePersistentFilters('lrReports.search', '');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkIssue, setShowBulkIssue] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Multi-select state
  const [selectedLRs, setSelectedLRs] = useState([]);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = usePersistentFilters('lrReports.filters', {
    bill_number: '',
    lr_number: '',
    supplier_id: '',
    quality_id: '',
    subquality_id: '',
    transport_id: '',
    lr_date_from: '',
    lr_date_to: '',
    status: '',
    driver_name: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeCompany?.company_id]);

  useEffect(() => {
    if (preSelectedOrderId) {
      setShowAddForm(true);
    }
  }, [preSelectedOrderId]);

  const fetchData = async () => {
    try {
      const [lrRes, ordersRes, suppliersRes, qualitiesRes, subqualitiesRes, transportsRes] = await Promise.allSettled([
        api.get('/lr-reports'),
        api.get('/grey-orders'),
        api.get('/suppliers'),
        api.get('/qualities'),
        api.get('/subqualities'),
        api.get('/transports')
      ]);
      setLrReports(lrRes.status === 'fulfilled' ? lrRes.value.data : []);
      setOrders(ordersRes.status === 'fulfilled' ? ordersRes.value.data : []);
      setSuppliers(suppliersRes.status === 'fulfilled' ? suppliersRes.value.data : []);
      setQualities(qualitiesRes.status === 'fulfilled' ? qualitiesRes.value.data : []);
      setSubqualities(subqualitiesRes.status === 'fulfilled' ? subqualitiesRes.value.data : []);
      setTransports(transportsRes.status === 'fulfilled' ? transportsRes.value.data : []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load LR reports');
      setLoading(false);
    }
  };

  const getOrderDetails = (orderId) => {
    return orders.find(o => o.order_id === orderId) || {};
  };

  // Apply filters
  const filteredLRs = lrReports.filter(lr => {
    const order = getOrderDetails(lr.order_id);
    
    // Search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        lr.lr_number?.toLowerCase().includes(searchLower) ||
        lr.lr_number_transport?.toLowerCase().includes(searchLower) ||
        lr.transport_name?.toLowerCase().includes(searchLower) ||
        order.order_number?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    // Advanced filters
    if (filters.bill_number && !lr.bill_number?.toLowerCase().includes(filters.bill_number.toLowerCase())) return false;
    if (filters.lr_number && !lr.lr_number_transport?.toLowerCase().includes(filters.lr_number.toLowerCase())) return false;
    if (filters.supplier_id && order.supplier_id !== filters.supplier_id) return false;
    if (filters.quality_id && order.quality_id !== filters.quality_id) return false;
    if (filters.subquality_id && order.subquality_id !== filters.subquality_id) return false;
    if (filters.transport_id && lr.transport_id !== filters.transport_id) return false;
    if (filters.status && lr.status !== filters.status) return false;
    if (filters.driver_name) {
      // TODO: This will be populated when we have driver info from issue history
    }
    
    // Date range filters
    if (filters.lr_date_from) {
      const lrDate = new Date(lr.lr_date);
      const fromDate = new Date(filters.lr_date_from);
      if (lrDate < fromDate) return false;
    }
    if (filters.lr_date_to) {
      const lrDate = new Date(lr.lr_date);
      const toDate = new Date(filters.lr_date_to);
      if (lrDate > toDate) return false;
    }
    
    return true;
  });

  const handleAddSuccess = () => {
    setShowAddForm(false);
    fetchData();
  };

  const handleBulkIssueSuccess = () => {
    setShowBulkIssue(false);
    setSelectedLRs([]);
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/lr-reports/${deleteTarget.lr_id}`);
      toast.success('LR Report deleted successfully!');
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete LR report');
    }
    setDeleting(false);
  };

  const handleSelectLR = (lrId, checked) => {
    if (checked) {
      setSelectedLRs([...selectedLRs, lrId]);
    } else {
      setSelectedLRs(selectedLRs.filter(id => id !== lrId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Only select LRs with status "In Transport"
      const issuableLRIds = filteredLRs
        .filter(lr => lr.status === 'In Transport')
        .map(lr => lr.lr_id);
      setSelectedLRs(issuableLRIds);
    } else {
      setSelectedLRs([]);
    }
  };

  const resetFilters = () => {
    setFilters({
      bill_number: '',
      lr_number: '',
      supplier_id: '',
      quality_id: '',
      subquality_id: '',
      transport_id: '',
      lr_date_from: '',
      lr_date_to: '',
      status: '',
      driver_name: ''
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;
  
  // Get selected LR details for bulk issue
  const selectedLRDetails = filteredLRs.filter(lr => selectedLRs.includes(lr.lr_id));
  const selectedLRNumbers = selectedLRDetails.map(lr => lr.lr_number);
  
  // Check if any selected LR is not issuable
  const nonIssuableSelected = selectedLRDetails.filter(lr => lr.status !== 'In Transport');

  // LR Status colors
  const statusColors = {
    'In Transport': 'bg-blue-50 text-blue-700 border-blue-200',
    'Issued for Delivery': 'bg-amber-50 text-amber-700 border-amber-200',
    'Delivered': 'bg-green-50 text-green-700 border-green-200'
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="lr-reports-list" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              LR Reports
            </h1>
            <p className="text-slate-600 mt-1">{filteredLRs.length} LR entries</p>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {/* Bulk Issue Button */}
            {selectedLRs.length > 0 && (
              <Sheet open={showBulkIssue} onOpenChange={setShowBulkIssue}>
                <Button 
                  data-testid="bulk-issue-btn"
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => {
                    if (nonIssuableSelected.length > 0) {
                      toast.error(`Cannot issue: ${nonIssuableSelected.map(lr => lr.lr_number).join(', ')} are already issued or delivered`);
                      return;
                    }
                    setShowBulkIssue(true);
                  }}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Issue Selected LRs ({selectedLRs.length})
                </Button>
                <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Bulk Issue LRs for Delivery</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <LRIssueForm 
                      lrIds={selectedLRs}
                      lrNumbers={selectedLRNumbers}
                      onSuccess={handleBulkIssueSuccess}
                      onCancel={() => setShowBulkIssue(false)}
                    />
                  </div>
                </SheetContent>
              </Sheet>
            )}
            
            <PermissionGuard module="lr_reports" permission="create">
            <Sheet open={showAddForm} onOpenChange={setShowAddForm}>
              <SheetTrigger asChild>
                <Button data-testid="add-lr-btn" className="bg-slate-900 hover:bg-slate-800">
                  <Plus className="h-4 w-4 mr-2" />
                  Add LR Report
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Add LR Report</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <LRReportForm 
                    orderId={preSelectedOrderId}
                    onSuccess={handleAddSuccess}
                    onCancel={() => setShowAddForm(false)}
                  />
                </div>
              </SheetContent>
            </Sheet>
            </PermissionGuard>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="search-lrs-input"
              placeholder="Search by LR number, transport, order..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
          
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="h-11"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge className="ml-2 bg-slate-900 text-white">{activeFilterCount}</Badge>
            )}
            {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
          </Button>
        </div>

        {/* Advanced Filters Panel */}
        <Collapsible open={showFilters} onOpenChange={setShowFilters}>
          <CollapsibleContent>
            <Card className="mb-4 border border-slate-200">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs">Bill Number</Label>
                    <Input
                      placeholder="Search bill..."
                      value={filters.bill_number}
                      onChange={(e) => setFilters({...filters, bill_number: e.target.value})}
                      className="h-9"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs">LR Number</Label>
                    <Input
                      placeholder="Search LR..."
                      value={filters.lr_number}
                      onChange={(e) => setFilters({...filters, lr_number: e.target.value})}
                      className="h-9"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs">Supplier</Label>
                    <Select value={filters.supplier_id} onValueChange={(v) => setFilters({...filters, supplier_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All suppliers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All suppliers</SelectItem>
                        {suppliers.map(s => (
                          <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Quality</Label>
                    <Select value={filters.quality_id} onValueChange={(v) => setFilters({...filters, quality_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All qualities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All qualities</SelectItem>
                        {qualities.map(q => (
                          <SelectItem key={q.quality_id} value={q.quality_id}>{q.quality_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Sub Quality</Label>
                    <Select value={filters.subquality_id} onValueChange={(v) => setFilters({...filters, subquality_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All sub qualities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All sub qualities</SelectItem>
                        {subqualities.map(sq => (
                          <SelectItem key={sq.subquality_id} value={sq.subquality_id}>{sq.subquality_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Transport</Label>
                    <Select value={filters.transport_id} onValueChange={(v) => setFilters({...filters, transport_id: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All transports" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All transports</SelectItem>
                        {transports.map(t => (
                          <SelectItem key={t.transport_id} value={t.transport_id}>{t.transport_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">LR Date From</Label>
                    <Input
                      type="date"
                      value={filters.lr_date_from}
                      onChange={(e) => setFilters({...filters, lr_date_from: e.target.value})}
                      className="h-9"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs">LR Date To</Label>
                    <Input
                      type="date"
                      value={filters.lr_date_to}
                      onChange={(e) => setFilters({...filters, lr_date_to: e.target.value})}
                      className="h-9"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v === 'all' ? '' : v})}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="In Transport">In Transport</SelectItem>
                        <SelectItem value="Issued for Delivery">Issued for Delivery</SelectItem>
                        <SelectItem value="Delivered">Delivered</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Driver Name</Label>
                    <Input
                      placeholder="Search driver..."
                      value={filters.driver_name}
                      onChange={(e) => setFilters({...filters, driver_name: e.target.value})}
                      className="h-9"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end mt-4 gap-2">
                  <Button variant="outline" size="sm" onClick={resetFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Active Filter Tags */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {filters.supplier_id && (
              <Badge variant="secondary" className="gap-1">
                Supplier: {suppliers.find(s => s.supplier_id === filters.supplier_id)?.name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, supplier_id: ''})} />
              </Badge>
            )}
            {filters.quality_id && (
              <Badge variant="secondary" className="gap-1">
                Quality: {qualities.find(q => q.quality_id === filters.quality_id)?.quality_name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, quality_id: ''})} />
              </Badge>
            )}
            {filters.status && (
              <Badge variant="secondary" className="gap-1">
                Status: {filters.status}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, status: ''})} />
              </Badge>
            )}
            {filters.transport_id && (
              <Badge variant="secondary" className="gap-1">
                Transport: {transports.find(t => t.transport_id === filters.transport_id)?.transport_name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, transport_id: ''})} />
              </Badge>
            )}
            {filters.lr_date_from && (
              <Badge variant="secondary" className="gap-1">
                From: {filters.lr_date_from}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, lr_date_from: ''})} />
              </Badge>
            )}
            {filters.lr_date_to && (
              <Badge variant="secondary" className="gap-1">
                To: {filters.lr_date_to}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({...filters, lr_date_to: ''})} />
              </Badge>
            )}
          </div>
        )}

        {/* Selection Info */}
        {selectedLRs.length > 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
            <span className="text-amber-800 font-medium">
              {selectedLRs.length} LR(s) selected
              {nonIssuableSelected.length > 0 && (
                <span className="text-amber-600 ml-2 text-sm">
                  ({nonIssuableSelected.length} already issued/delivered)
                </span>
              )}
            </span>
            <Button variant="ghost" size="sm" onClick={() => setSelectedLRs([])}>
              Clear selection
            </Button>
          </div>
        )}
      </div>

      {/* LR Reports Table */}
      <Card className="border border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedLRs.length > 0 && selectedLRs.length === filteredLRs.filter(lr => lr.status === 'In Transport').length}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>LR #</TableHead>
                  <TableHead>Order #</TableHead>
                  <TableHead>Transport LR</TableHead>
                  <TableHead>Transport</TableHead>
                  <TableHead>LR Date</TableHead>
                  <TableHead className="text-right">Bales</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLRs.length > 0 ? (
                  filteredLRs.map((lr) => {
                    const order = getOrderDetails(lr.order_id);
                    const isSelected = selectedLRs.includes(lr.lr_id);
                    const canIssue = lr.status === 'In Transport';
                    
                    return (
                      <TableRow 
                        key={lr.lr_id} 
                        data-testid={`lr-row-${lr.lr_number}`}
                        className={`cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-amber-50/50' : ''}`}
                        onClick={() => navigate(`/lr-reports/${lr.lr_id}`)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            disabled={!canIssue}
                            onCheckedChange={(checked) => handleSelectLR(lr.lr_id, checked)}
                          />
                        </TableCell>
                        <TableCell className="font-mono font-medium">{lr.lr_number}</TableCell>
                        <TableCell>
                          {hasModulePermission('grey_orders', 'view') ? (
                            <Badge 
                              variant="outline" 
                              className="cursor-pointer hover:bg-slate-100"
                              onClick={(e) => { e.stopPropagation(); navigate(`/grey-orders/${lr.order_id}`); }}
                            >
                              {order.order_number || lr.order_id}
                            </Badge>
                          ) : (
                            <span className="text-sm text-slate-600">{order.order_number || lr.order_id}</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{lr.lr_number_transport}</TableCell>
                        <TableCell>{lr.transport_name}</TableCell>
                        <TableCell>{formatDate(lr.lr_date)}</TableCell>
                        <TableCell className="text-right font-medium">{lr.bales_in_lr}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[lr.status] || 'bg-slate-100 text-slate-700'}>
                            {lr.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{lr.created_by_name}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                      <Truck className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                      No LR reports found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default LRReportsList;
